import {useMemo, useState} from 'react'
import {useSearchParams} from 'react-router'
import classNames from 'classnames'
import {Navbar} from '@product/quies/components/Navbar/Navbar'
import {SectionWrapper} from '@ui/SectionWrapper/SectionWrapper'
import {PricingItem} from '@product/quies/components/PricingItem/PricingItem'
import type {Pricing} from '@product/quies/services/pricing.server'
import {Failure} from '@product/quies/components/Failures'
import {Icon} from '@ui/Icon/icon'
import {buildFAQPage, buildWebApplication} from '@core/lib/jsonld'
import {buildProofPage} from '@core/proof/model'
import {ProofPage} from '@core/proof/ProofPage'
import type {HomeContent, HomeFaqItem} from '@product/quies/data/home-content'
import {quiesConfig} from '@product/quies/config'
import {CATALOGUE, interfaceLang} from '@product/quies/i18n/catalogue'
import {quiesContent} from '@product/quies/proof/content'
import {quiesParts} from '@product/quies/proof/parts'
import {quiesCopy, quiesProofSpecs} from '@product/quies/proof/spec'
import styles from '@product/quies/styles/Home/_layout._index.module.scss'

export type HomePageProps = {
    content: HomeContent
    canonicalUrl: string
    pricings: Pricing[]
}

/** The home of a language: the feature page (`../../proof/`), then the plans, then the questions. */
export const HomePage = ({content, canonicalUrl, pricings}: HomePageProps) => {
    const {lang, structuredData, faq} = content

    // The WebApplication url has always been slash-free, the root's included
    // ("https://quies.ai"), though the root's canonical is "https://quies.ai/".
    const webAppUrl = canonicalUrl.endsWith('/') ? canonicalUrl.slice(0, -1) : canonicalUrl

    const webAppJsonLd = {
        ...buildWebApplication({
            name: quiesConfig.name,
            description: structuredData.description,
            url: webAppUrl,
            applicationCategory: 'HealthApplication',
            offers: {price: '0', priceCurrency: 'USD', description: structuredData.offerDescription},
            featureList: structuredData.featureList,
            keywords: structuredData.keywords,
            screenshot: quiesConfig.meta.ogImage,
        }),
        ...(structuredData.inLanguage && {inLanguage: structuredData.inLanguage}),
    }
    const faqJsonLd = buildFAQPage(faq.items.map((item) => ({q: item.question, a: item.answer})))

    const data = useMemo(() => buildProofPage(quiesProofSpecs[lang], quiesContent(lang)), [lang])
    const plans = quiesCopy(lang).plans

    return (
        <>
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(webAppJsonLd)}}/>
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(faqJsonLd)}}/>
            <ProofPage
                data={data}
                header={<Navbar variant="public" />}
                parts={quiesParts(lang)}
                footer={
                    <>
                        <SectionWrapper id="pricing" className={styles.hashTarget}>
                            <div className={styles.pricing}>
                                <div className={styles.pricingHeader}>
                                    <h2>{plans.heading}</h2>
                                    <p>{plans.body}</p>
                                </div>
                                <div className={styles.pricingList}>
                                    {pricings.map((pricing) => (
                                        <PricingItem
                                            key={pricing.id}
                                            title={pricing.name}
                                            features={pricing.features}
                                            emphasis={pricing.emphasis}
                                            price={pricing.price}
                                            currency={pricing.currency}
                                            token={pricing.tokens}
                                            priceId={pricing.id}
                                            recurring={pricing.recurring}
                                        />
                                    ))}
                                </div>
                                <p className={styles.pricingNote}>{CATALOGUE[interfaceLang(lang)].pricing.replayFree}</p>
                                <CheckoutFailed />
                            </div>
                        </SectionWrapper>
                        <SectionWrapper id="faq" className={styles.hashTarget}>
                            <div className={styles.faq}>
                                <h2>{faq.heading}</h2>
                                <FaqAccordion items={faq.items} />
                            </div>
                        </SectionWrapper>
                    </>
                }
            />
        </>
    )
}

function FaqAccordion({items}: {items: HomeFaqItem[]}) {
    const [openIndex, setOpenIndex] = useState(0)

    return (
        <div className={styles.faqList}>
            {items.map((item, i) => {
                const isOpen = i === openIndex
                const buttonId = `faq-question-${i}`
                const panelId = `faq-answer-${i}`
                return (
                    <div key={i} className={styles.faqItem}>
                        <h3 className={styles.faqQuestionHeading}>
                            <button
                                type="button"
                                id={buttonId}
                                className={styles.faqQuestion}
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => setOpenIndex(isOpen ? -1 : i)}
                            >
                                <span>{item.question}</span>
                                <Icon
                                    id="qs-chevron-down"
                                    size={16}
                                    outline
                                    className={classNames(styles.faqChevron, {[styles.faqChevronOpen]: isOpen})}
                                />
                            </button>
                        </h3>
                        <div
                            id={panelId}
                            role="region"
                            aria-labelledby={buttonId}
                            className={classNames(styles.faqAnswer, {[styles.faqAnswerOpen]: isOpen})}
                        >
                            <div><p>{item.answer}</p></div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

/** A checkout that could not start returns here (`checkout.$priceId`): the plans stay, Select Plan
 *  is the retry (Failures spec, case 16). */
function CheckoutFailed() {
    const [searchParams] = useSearchParams()
    if (searchParams.get('checkout') !== 'failed') return null
    return <Failure failure="checkout_failed" handlers={{}} />
}
